import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { Panel, fmtDate } from "@/components/shared/DashKit";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock, Phone, Plus, Repeat } from "lucide-react";

const inputCls = "w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50";
const codeCls = "w-full max-w-[260px] rounded-xl border-2 border-border bg-background px-3 py-3 text-center font-mono text-2xl font-bold tracking-[0.45em] outline-none placeholder:text-base placeholder:font-normal placeholder:tracking-normal focus:border-accent focus:ring-2 focus:ring-accent/40 sm:text-3xl";
const btn = "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50";

function PrimaryForm({ onDone }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post("/me/payment-numbers/primary", { phone }); toast.success(t("pn.primary_saved")); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="pn-primary-form">
      <p className="text-sm text-muted-foreground">{t("pn.no_primary")}</p>
      <div className="flex gap-2">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+243970000000" data-testid="pn-primary-input" className={inputCls} />
        <button onClick={save} disabled={busy || phone.length < 8} data-testid="pn-primary-save" className={`${btn} bg-primary text-primary-foreground hover:bg-accent`}>{busy && <Loader2 className="h-3 w-3 animate-spin" />}{t("pn.save")}</button>
      </div>
    </div>
  );
}

const OTP_ERR = [
  ["Code expiré ou inexistant", "pn.err.expired"], ["Trop de tentatives", "pn.err.attempts"], ["Code incorrect", "pn.err.incorrect"],
  ["Code déjà utilisé", "pn.err.used"], ["Trop de codes demandés", "pn.err.rate_number"], ["Limite quotidienne d'envoi", "pn.err.rate_daily"],
  ["Envoi SMS indisponible", "pn.sms_unavailable"], ["L'envoi du SMS a échoué", "pn.err.send_failed"], ["Ce numéro n'est pas en attente", "pn.err.not_pending"],
];
const otpError = (t, e) => {
  const msg = apiError(e.response?.data?.detail);
  const hit = OTP_ERR.find(([fr]) => msg.startsWith(fr));
  return hit ? t(hit[1]) : msg;
};

function CodeEntry({ n, onDone, smsAvailable }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [busy, setBusy] = useState("");
  const send = async () => {
    setBusy("send");
    try {
      const r = await api.post(`/me/payment-numbers/${n.id}/send-otp`);
      if (r.data?.simulated_code) toast.info(`${t("pn.sent_sim")} ${r.data.simulated_code}`, { duration: 15000 });
      else toast.success(t(r.data?.uncertain ? "pn.sent_uncertain" : "pn.sent"));
      onDone();
    }
    catch (e) { toast.error(otpError(t, e)); } finally { setBusy(""); }
  };
  const verify = async () => {
    setBusy("verify");
    try { const { data } = await api.post(`/me/payment-numbers/${n.id}/verify`, { code, email_code: emailCode || undefined }); toast.success(data.pending_approval ? t("pn.otp_ok_pending") : t("pn.verified_ok")); onDone(); }
    catch (e) { toast.error(otpError(t, e)); } finally { setBusy(""); }
  };
  const canSend = n.is_primary || n.code_pending;
  return (
    <div className="mt-2 space-y-2">
      {n.code_pending && (
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end" data-testid={`pn-code-entry-${n.id}`}>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">{t("pn.code_sms")}
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="••••••" aria-label={t("pn.code_sms")} data-testid={`pn-code-${n.id}`} className={codeCls} />
          </label>
          {n.email_code_required && <label className="flex flex-col gap-1 text-xs text-muted-foreground">{t("pn.code_email")}
            <input value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="••••••" aria-label={t("pn.code_email")} data-testid={`pn-email-code-${n.id}`} className={codeCls} />
          </label>}
          <button onClick={verify} disabled={busy || code.length !== 6} data-testid={`pn-verify-${n.id}`} className={`${btn} bg-primary text-primary-foreground hover:bg-accent`}>{busy === "verify" && <Loader2 className="h-3 w-3 animate-spin" />}{t("pn.verify")}</button>
          <p className="w-full text-xs text-muted-foreground" data-testid={`pn-code-hint-${n.id}`}>{t("pn.code_hint")}</p>
        </div>
      )}
      {canSend ? (
        <button onClick={send} disabled={busy || !smsAvailable} data-testid={`pn-send-${n.id}`} className={`${btn} border border-border hover:bg-secondary`}>{busy === "send" && <Loader2 className="h-3 w-3 animate-spin" />}{n.code_pending ? t("pn.resend") : t("pn.send")}</button>
      ) : <p className="text-xs text-muted-foreground">{t("pn.wait_support")}</p>}
    </div>
  );
}

function SupportVerify({ n, onDone }) {
  const { t } = useTranslation();
  const [op, setOp] = useState("");
  const [busy, setBusy] = useState(false);
  if (n.support_requested) return <p className="text-xs text-muted-foreground" data-testid={`pn-support-pending-${n.id}`}>{t("pn.support_pending")}</p>;
  const submit = async () => {
    setBusy(true);
    try { await api.post("/me/payment-numbers/requests", { type: "verify", operator: op || undefined }); toast.success(t("pn.req_sent")); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-testid={`pn-support-verify-${n.id}`}>
      <select value={op} onChange={(e) => setOp(e.target.value)} data-testid={`pn-support-operator-${n.id}`} className={`${inputCls} sm:!w-48`}>
        <option value="">{t("pn.operator")}</option>
        {(n.operators || []).map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
      </select>
      <button onClick={submit} disabled={busy || !op} data-testid={`pn-support-request-${n.id}`} className={`${btn} border border-border hover:bg-secondary`}>{busy && <Loader2 className="h-3 w-3 animate-spin" />}{t("pn.ask_support")}</button>
    </div>
  );
}

function NumberRow({ n, onDone, smsAvailable }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-border p-3" data-testid={`pn-number-${n.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-muted-foreground" /><span className="font-mono">+{n.msisdn}</span>
          {n.is_primary && <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent">{t("pn.primary")}</span>}</div>
        {n.status === "verified"
          ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600" data-testid={`pn-status-${n.id}`}><CheckCircle2 className="h-3.5 w-3.5" />{t("pn.verified")}</span>
          : <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600" data-testid={`pn-status-${n.id}`}><Clock className="h-3.5 w-3.5" />{t("pn.unverified")}</span>}
      </div>
      {n.note && <p className="mt-1 text-xs text-muted-foreground">{n.note}</p>}
      {n.status !== "verified" && <CodeEntry n={n} onDone={onDone} smsAvailable={smsAvailable} />}
      {n.status !== "verified" && n.is_primary && <div className="mt-2"><SupportVerify n={n} onDone={onDone} /></div>}
    </div>
  );
}

function RequestForm({ data, onDone, onCancel }) {
  const { t } = useTranslation();
  const verified = data.numbers.filter((n) => n.status === "verified");
  const [f, setF] = useState({ type: data.verified_count < data.max_verified ? "add" : "replace", phone: "", target_number_id: verified[0]?.id || "", reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try { await api.post("/me/payment-numbers/requests", f); toast.success(t("pn.req_sent")); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3" data-testid="pn-request-form">
      <select value={f.type} onChange={set("type")} data-testid="pn-req-type" className={inputCls}>
        <option value="add" disabled={data.verified_count >= data.max_verified}>{t("pn.req_add")}</option>
        <option value="replace" disabled={!verified.length}>{t("pn.req_replace")}</option>
        <option value="remove" disabled={!verified.some((n) => !n.is_primary)}>{t("pn.req_remove")}</option>
      </select>
      {f.type !== "add" && (
        <select value={f.target_number_id} onChange={set("target_number_id")} data-testid="pn-req-target" className={inputCls}>
          {verified.filter((n) => f.type === "replace" || !n.is_primary).map((n) => <option key={n.id} value={n.id}>+{n.msisdn}{n.is_primary ? t("pn.primary_suffix") : ""}</option>)}
        </select>
      )}
      {f.type !== "remove" && <input value={f.phone} onChange={set("phone")} placeholder={t("pn.new_phone")} data-testid="pn-req-phone" className={inputCls} />}
      <textarea value={f.reason} onChange={set("reason")} rows={2} placeholder={f.type === "add" ? t("pn.note_optional") : t("pn.reason_required")} data-testid="pn-req-reason" className={inputCls} />
      {f.type === "replace" && verified.find((n) => n.id === f.target_number_id)?.is_primary && <p className="text-xs text-amber-600">{t("pn.reinforced")}</p>}
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} data-testid="pn-req-submit" className={`${btn} bg-primary text-primary-foreground hover:bg-accent`}>{busy && <Loader2 className="h-3 w-3 animate-spin" />}{t("pn.submit")}</button>
        <button onClick={onCancel} data-testid="pn-req-cancel" className={`${btn} border border-border hover:bg-secondary`}>{t("pn.cancel")}</button>
      </div>
    </div>
  );
}

export function PaymentNumbers() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(false);
  const load = useCallback(() => { api.get("/me/payment-numbers").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const hasPrimary = data.numbers.some((n) => n.is_primary);
  return (
    <Panel title={t("pn.title")} className="mt-6">
      <div className="space-y-3" data-testid="payment-numbers-section">
        <p className="text-xs text-muted-foreground">{t("pn.count", { n: data.verified_count, max: data.max_verified })}</p>
        {!data.sms_available && <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700" data-testid="pn-sms-unavailable">{t("pn.sms_unavailable")}</p>}
        {!hasPrimary && <PrimaryForm onDone={load} />}
        {data.numbers.map((n) => <NumberRow key={n.id} n={n} onDone={load} smsAvailable={data.sms_available} />)}
        {form ? <RequestForm data={data} onDone={() => { setForm(false); load(); }} onCancel={() => setForm(false)} /> : (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setForm(true)} disabled={data.verified_count >= data.max_verified || !data.verified_count} data-testid="pn-request-add-btn" className={`${btn} border border-border hover:bg-secondary`}><Plus className="h-3.5 w-3.5" />{t("pn.btn_add")}</button>
            <button onClick={() => setForm(true)} disabled={!data.verified_count} data-testid="pn-request-change-btn" className={`${btn} border border-border hover:bg-secondary`}><Repeat className="h-3.5 w-3.5" />{t("pn.btn_change")}</button>
          </div>
        )}
        {data.requests.length > 0 && (
          <div className="space-y-2 pt-2" data-testid="pn-requests">
            <p className="text-xs font-medium">{t("pn.my_requests")}</p>
            {data.requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-secondary/40 px-3 py-2 text-xs" data-testid={`pn-request-${r.id}`}>
                <span>{t(`pn.type.${r.type}`)}{r.msisdn ? ` · +${r.msisdn}` : ""} · {fmtDate(r.created_at)}</span>
                <span className="font-medium">{t(`pn.st.${r.status}`)}{r.status === "rejected" && r.status_reason ? ` — ${r.status_reason}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
